import React, { useState, useEffect, useCallback } from "react";
import { TrashIcon } from "@heroicons/react/24/solid";
import SearchBar from "../../ui/SearchBar";
import { PageLoadingView } from "../../common/PageLoadingView";
import { PageErrorView } from "../../common/PageErrorView";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import { useFacilities } from "../../../hooks/useFacilities";
import { useUser } from "../../../context/UserInfoProvider";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { DataManagementSidebar } from "./subcomponents/DataManagementSidebar";
import {
  FacilityItem,
  FacilityType,
  FacilityStatus,
  FacilityFKReferenceError,
} from "../../../api/facilitiesApi";
import Input from "../../form/input/InputField";
import ImageUploader from "../../form/ImageUploader";

interface ValidationErrors {
  name?: string;
  description?: string;
  type?: string;
  status?: string;
  capacity?: string;
  operatingHours?: string;
  headerPhoto?: string;
}

const FacilitiesData: React.FC = () => {
  const { hotelId } = useUser();
  const analytics = useAnalytics();

  // Use the updated hook instead of local state management
  const {
    items,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    pagination,
    setPage,
    setLimit,
    setSearchTerm,
    refetchItems,
  } = useFacilities({ hotelId: hotelId || "" });

  // UI-specific state (keep in component)
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null); // Separate state for delete errors
  const [isCreating, setIsCreating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [operatingHoursStart, setOperatingHoursStart] = useState<string>("");
  const [operatingHoursEnd, setOperatingHoursEnd] = useState<string>("");
  const [pendingHeaderPhotoKey, setPendingHeaderPhotoKey] = useState<
    string | null
  >(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const validateFacility = (
    item: Partial<FacilityItem>,
    requireOperatingHours: boolean = false
  ): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!item.name?.trim()) {
      errors.name = "Name is required";
    }

    if (!item.description?.trim()) {
      errors.description = "Description is required";
    }

    if (!item.type) {
      errors.type = "Type is required";
    }

    if (!item.status) {
      errors.status = "Status is required";
    }

    // Capacity required and must be a non-negative integer
    if (
      item.capacity === undefined ||
      item.capacity === null ||
      Number.isNaN(item.capacity as unknown as number)
    ) {
      errors.capacity = "Capacity is required";
    } else if (typeof item.capacity !== "number" || item.capacity < 0) {
      errors.capacity = "Capacity cannot be negative";
    } else if (!Number.isInteger(item.capacity)) {
      errors.capacity = "Capacity must be an integer";
    }

    // Operating hours are optional; validate format only when provided
    if (requireOperatingHours && !item.operatingHours?.trim()) {
      errors.operatingHours = "Operating hours are required";
    }

    if (item.operatingHours && item.operatingHours.trim()) {
      const timeRegex =
        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]-([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(item.operatingHours)) {
        errors.operatingHours = "Operating hours must be in format HH:MM-HH:MM";
      } else {
        const [startStr, endStr] = item.operatingHours.split("-");
        const toMinutes = (timeStr: string) => {
          const [hours, minutes] = timeStr.split(":").map(Number);
          return hours * 60 + minutes;
        };
        if (toMinutes(startStr) >= toMinutes(endStr)) {
          errors.operatingHours = "Start time must be before end time.";
        }
      }
    }

    return errors;
  };

  const handleItemSelect = (item: FacilityItem) => {
    // If we're creating a new facility and user clicks an existing one, warn them
    if (isCreating) {
      setConfirmationDialog({
        isOpen: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes for a new facility. Do you want to discard these changes and edit the selected facility?",
        confirmText: "Discard Changes",
        cancelText: "Cancel",
        onConfirm: () => {
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
          // Execute the original logic
          setSelectedItem(item);
          setValidationErrors({});
          setUpdateError(null);
          setDeleteError(null);
          setIsCreating(false);
          setPendingHeaderPhotoKey(null);
          if (item.operatingHours) {
            const parts = item.operatingHours.split("-");
            if (parts.length === 2) {
              setOperatingHoursStart(parts[0]);
              setOperatingHoursEnd(parts[1]);
            } else {
              setOperatingHoursStart("");
              setOperatingHoursEnd("");
            }
          } else {
            setOperatingHoursStart("");
            setOperatingHoursEnd("");
          }
        },
      });
      return;
    }

    setSelectedItem(item);
    setValidationErrors({});
    setUpdateError(null); // Clear any previous sidebar errors
    setDeleteError(null); // Clear any previous delete errors
    setIsCreating(false); // Switch to edit mode
    setPendingHeaderPhotoKey(null);
    if (item.operatingHours) {
      const parts = item.operatingHours.split("-");
      if (parts.length === 2) {
        setOperatingHoursStart(parts[0]);
        setOperatingHoursEnd(parts[1]);
      } else {
        setOperatingHoursStart("");
        setOperatingHoursEnd("");
      }
    } else {
      setOperatingHoursStart("");
      setOperatingHoursEnd("");
    }
  };

  const handleAddItem = () => {
    const newItem: FacilityItem = {
      facilityId: "",
      hotelId: hotelId || "",
      name: "",
      description: "",
      type: "Indoor" as FacilityType,
      capacity: 0,
      status: "Available" as FacilityStatus,
      operatingHours: "", // Start with empty hours
      headerPhoto: "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setSelectedItem(newItem);
    setOperatingHoursStart(""); // Start with empty time fields
    setOperatingHoursEnd("");
    setValidationErrors({});
    setUpdateError(null); // Clear sidebar errors
    setDeleteError(null); // Clear delete errors
    setIsCreating(true);
    setPendingHeaderPhotoKey(null);
  };

  // Store pending image for both new and existing facilities - only update DB on save
  const handleLocalHeaderUpload = useCallback(
    async (key: string, previewUrl?: string) => {
      // Store the S3 key for saving to the database
      setPendingHeaderPhotoKey(key);

      // Store the preview URL for immediate display
      setSelectedItem((prev: FacilityItem | null) => {
        if (!prev) return null;
        return { ...prev, headerPhoto: previewUrl || key };
      });
    },
    []
  );

  useEffect(() => {
    if (selectedItem) {
      if (selectedItem.operatingHours) {
        const parts = selectedItem.operatingHours.split("-");
        if (parts.length === 2) {
          setOperatingHoursStart(parts[0]);
          setOperatingHoursEnd(parts[1]);
        } else {
          setOperatingHoursStart("");
          setOperatingHoursEnd("");
        }
      } else {
        setOperatingHoursStart("");
        setOperatingHoursEnd("");
      }
    }
  }, [selectedItem]);

  const handleSave = async () => {
    if (!selectedItem) return;

    let finalOperatingHours = "";
    if (operatingHoursStart && operatingHoursEnd) {
      finalOperatingHours = `${operatingHoursStart}-${operatingHoursEnd}`;
    } else if (operatingHoursStart || operatingHoursEnd) {
      // If one time is provided, require both or none
      setValidationErrors((prev) => ({
        ...prev,
        operatingHours:
          "Please provide both a start and end time, or leave this field blank.",
      }));
      return;
    }

    const itemToValidate: FacilityItem = {
      ...selectedItem,
      operatingHours: finalOperatingHours,
    };

    const errors = validateFacility(itemToValidate, false);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      setIsUpdating(true);
      setUpdateError(null);
      setValidationErrors({});

      const {
        facilityId,
        name,
        description,
        type,
        capacity,
        status,
        operatingHours,
      } = itemToValidate;

      const wasCreating = isCreating;

      if (isCreating) {
        // Step 1: Create new facility without header photo
        const createdFacility = await createItem({
          hotelId: hotelId || "",
          name: name || "",
          description: description || "",
          type: type || "Both",
          capacity: capacity || 0,
          status: status || "Available",
          operatingHours: finalOperatingHours, // Pass empty string if no times set
          headerPhoto: null, // Don't include header photo in creation
        });

        // Step 2: If there's a header photo to upload, update the facility with it
        if (pendingHeaderPhotoKey) {
          try {
            await updateItem(createdFacility.facilityId, {
              headerPhoto: pendingHeaderPhotoKey,
            });
          } catch (imageError) {
            console.error(
              "Error updating header photo after creation:",
              imageError
            );
            setUpdateError(
              "Facility created successfully, but failed to upload header photo. You can upload it later."
            );
          }
        }

        // Refresh to get the new item in the list with signed URLs
        await refetchItems();

        setIsCreating(false);
      } else {
        // Update existing facility using hook
        const updates: Partial<FacilityItem> = {};

        if (name !== undefined) updates.name = name;
        if (description !== undefined) updates.description = description;
        if (type !== undefined) updates.type = type;
        if (status !== undefined) updates.status = status;
        if (capacity !== undefined) updates.capacity = capacity;
        // Handle headerPhoto if there's a pending upload OR if it was deleted (set to null)
        if (pendingHeaderPhotoKey) {
          updates.headerPhoto = pendingHeaderPhotoKey;
        } else if (selectedItem.headerPhoto === null) {
          // Image was deleted, explicitly set to null
          updates.headerPhoto = null;
        }
        if (operatingHours !== undefined)
          updates.operatingHours = operatingHours;

        // Update the facility
        await updateItem(facilityId, updates);

        // Refresh the item to get the updated facility with signed URLs from the list endpoint
        await refetchItems();
      }

      // Track facility management analytics
      analytics.staff(wasCreating ? "facility_created" : "facility_updated", {
        facility_type: type?.toLowerCase(),
        facility_status: status?.toLowerCase(),
        has_capacity: !!capacity,
        has_operating_hours: !!operatingHours,
        has_header_photo: !!pendingHeaderPhotoKey,
      });

      // Close sidebar after save
      setSelectedItem(null);
      setPendingHeaderPhotoKey(null);
    } catch (err) {
      console.error("Error saving facility:", err);
      if (
        err instanceof Error &&
        err.message.includes("uq_facility_hotel_name")
      ) {
        setUpdateError("This facility already exists.");
      } else {
        setUpdateError(
          err instanceof Error ? err.message : "Failed to save facility"
        );
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    const target = items.find((i) => i.facilityId === itemId);
    const facilityName = target?.name || "this facility";

    setConfirmationDialog({
      isOpen: true,
      title: "Delete Facility",
      message: `Are you sure you want to delete "${facilityName}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));

        try {
          setIsUpdating(true);
          setDeleteError(null); // Clear any previous delete errors
          await deleteItem(itemId);

          if (selectedItem?.facilityId === itemId) {
            setSelectedItem(null);
          }

          // Track facility deletion analytics
          analytics.staff("facility_deleted", {
            facility_id: itemId,
          });
        } catch (err: any) {
          console.error("Error deleting facility:", err);
          // Detect FK/constraint/reference errors either via typed error or message text
          const errMsg =
            err?.response?.data?.error?.message ||
            (err instanceof Error ? err.message : "");
          const looksLikeFkError =
            /foreign key|constraint|referenced|cannot delete/i.test(errMsg);

          if (err instanceof FacilityFKReferenceError || looksLikeFkError) {
            // Show second confirmation dialog for FK reference error
            setConfirmationDialog({
              isOpen: true,
              title: "Cannot Delete Facility",
              message:
                `Cannot delete "${facilityName}" because it is referenced by other records.\n\n` +
                `Would you like to set the status to "Closed" instead? This will prevent new reservations while keeping existing data.`,
              confirmText: "Set to Closed",
              cancelText: "Cancel",
              onConfirm: async () => {
                setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
                if (target) {
                  try {
                    // Update the facility status to "Closed"
                    await updateItem(target.facilityId, {
                      status: "Closed" as FacilityStatus,
                    });
                    setDeleteError(null);
                    // Refetch to get updated status
                    await refetchItems();
                  } catch (statusError) {
                    setDeleteError(
                      "Failed to update facility status. Please try again."
                    );
                    console.error("Error updating status:", statusError);
                  }
                }
              },
            });
          } else {
            setDeleteError(
              err instanceof Error ? err.message : "Failed to delete facility"
            );
          }
        } finally {
          setIsUpdating(false);
        }
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    if ([5, 10, 20, 50].includes(newLimit)) {
      setLimit(newLimit);
      // Refresh items so pagination.total reflects the new limit
      // and the UI can compute an accurate totalPages value
      refetchItems();
    }
  };

  // Format operating hours ("HH:MM-HH:MM") to standard 12-hour format (e.g., "9:00 AM - 5:30 PM")
  const formatOperatingHours12h = (operatingHours?: string): string => {
    if (!operatingHours || !operatingHours.trim()) return "N/A";
    const [startStr, endStr] = operatingHours.split("-");
    if (!startStr || !endStr) return operatingHours;

    const to12h = (time: string): string => {
      const [hStr, mStr] = time.split(":");
      const h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      if (Number.isNaN(h) || Number.isNaN(m)) return time;
      const suffix = h >= 12 ? "PM" : "AM";
      const hour12 = ((h + 11) % 12) + 1;
      const mm = m.toString().padStart(2, "0");
      return `${hour12}:${mm} ${suffix}`;
    };

    try {
      return `${to12h(startStr)} - ${to12h(endStr)}`;
    } catch {
      return operatingHours;
    }
  };

  // Items are now filtered server-side via searchTerm
  const filteredItems = items;

  // Derive total pages consistently (mirrors SpecialEventsData behavior)
  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  if (isLoading && !selectedItem) {
    return <PageLoadingView />;
  }

  return (
    <div className="w-full h-screen flex flex-col text-gray-800 dark:text-white overflow-hidden">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Facilities</h1>
        <button
          onClick={handleAddItem}
          className="bg-blue-600 text-white py-1 px-4 rounded hover:bg-blue-500"
        >
          + Add Facility
        </button>
      </div>

      {/* Search Bar */}
      <div className="px-6">
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={(q) => {
            setSearchQuery(q);
            if (q === "") {
              setSearchTerm("");
            }
          }}
          onSearchSubmit={() => {
            setIsSearching(true);
            setSearchTerm(searchQuery);
            // Track facility search
            analytics.search(searchQuery, items.length, "facilities");
            setTimeout(() => setIsSearching(false), 100);
          }}
          isLoading={isSearching}
          placeholder="Search for a facility"
        />
        {deleteError && (
          <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded relative">
            <span className="block sm:inline">{deleteError}</span>
            <button
              className="absolute top-0 bottom-0 right-0 px-4 py-3 text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100"
              onClick={() => setDeleteError(null)}
            >
              <span className="sr-only">Dismiss</span>
              <span className="text-xl">&times;</span>
            </button>
          </div>
        )}
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto p-6">
        {error ? (
          <PageErrorView
            title="Error loading facilities"
            onRetry={refetchItems}
          />
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchQuery
              ? "No facilities match your search"
              : "No facilities found"}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredItems.map((item) => (
              <div
                key={item.facilityId}
                className="py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => handleItemSelect(item)}
              >
                {/* Image preview aligned like special events; placeholder matches menu items */}
                <div className="w-10 h-10 rounded overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-4">
                  {item.headerPhoto ? (
                    <img
                      key={item.headerPhoto}
                      src={item.headerPhoto}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Hide image on error
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                      }}
                    />
                  ) : (
                    <svg
                      className="w-6 h-6 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium">{item.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Type: {item.type} | Status: {item.status} | Capacity:{" "}
                    {item.capacity} | Hours: {formatOperatingHours12h(item.operatingHours)}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.facilityId);
                  }}
                  className="text-red-500 hover:text-red-600"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Show
            </span>
            <select
              value={pagination.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 pr-8"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              items per page
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            <span className="text-sm">Page {pagination.page} of {totalPages}</span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <DataManagementSidebar
        title={isCreating ? "Add Facility" : "Edit Facility"}
        selectedItem={selectedItem}
        onClose={async () => {
          setSelectedItem(null);
          setIsCreating(false);
          setValidationErrors({});
          setUpdateError(null);
          setDeleteError(null);
          setPendingHeaderPhotoKey(null);
        }}
        onSave={handleSave}
        isUpdating={isUpdating}
        updateError={updateError}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={selectedItem?.name || ""}
              onChange={(e) =>
                setSelectedItem((prev: FacilityItem | null) =>
                  prev ? { ...prev, name: e.target.value } : null
                )
              }
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                validationErrors.name ? "border-red-500" : ""
              }`}
              placeholder="Enter facility name"
            />
            {validationErrors.name && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={selectedItem?.description || ""}
              onChange={(e) =>
                setSelectedItem((prev: FacilityItem | null) =>
                  prev ? { ...prev, description: e.target.value } : null
                )
              }
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                validationErrors.description ? "border-red-500" : ""
              }`}
              placeholder="Enter description"
              rows={3}
            />
            {validationErrors.description && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.description}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedItem?.type || ""}
              onChange={(e) =>
                setSelectedItem((prev: FacilityItem | null) =>
                  prev
                    ? { ...prev, type: e.target.value as FacilityType }
                    : null
                )
              }
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                validationErrors.type ? "border-red-500" : ""
              }`}
            >
              <option value="Indoor">Indoor</option>
              <option value="Outdoor">Outdoor</option>
              <option value="Both">Both</option>
            </select>
            {validationErrors.type && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.type}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedItem?.status || ""}
              onChange={(e) =>
                setSelectedItem((prev: FacilityItem | null) =>
                  prev
                    ? { ...prev, status: e.target.value as FacilityStatus }
                    : null
                )
              }
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                validationErrors.status ? "border-red-500" : ""
              }`}
            >
              <option value="Available">Available</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Closed">Closed</option>
            </select>
            {validationErrors.status && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.status}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Capacity <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={selectedItem?.capacity || ""}
              onChange={(e) =>
                setSelectedItem((prev: FacilityItem | null) =>
                  prev ? { ...prev, capacity: parseInt(e.target.value) } : null
                )
              }
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                validationErrors.capacity ? "border-red-500" : ""
              }`}
              min="0"
              required
            />
            {validationErrors.capacity && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.capacity}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Operating Hours (Start - End)
            </label>
            <div className="mt-1 flex items-center space-x-2">
              <Input
                type="time"
                value={operatingHoursStart}
                onChange={(e) => {
                  setOperatingHoursStart(e.target.value);
                  if (validationErrors.operatingHours) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      operatingHours: undefined,
                    }));
                  }
                }}
                className={`block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                  validationErrors.operatingHours ? "border-red-500" : ""
                }`}
              />
              <span className="text-gray-500 dark:text-gray-400">-</span>
              <Input
                type="time"
                value={operatingHoursEnd}
                onChange={(e) => {
                  setOperatingHoursEnd(e.target.value);
                  if (validationErrors.operatingHours) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      operatingHours: undefined,
                    }));
                  }
                }}
                className={`block w-60 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                  validationErrors.operatingHours ? "border-red-500" : ""
                }`}
              />
            </div>
            {validationErrors.operatingHours && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.operatingHours}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Header Photo
            </label>
            {isCreating ? (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center mt-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please save the facility first to upload a photo
                </p>
              </div>
            ) : !selectedItem?.facilityId ? (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center mt-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Facility ID is missing. Please refresh the page or select a
                  different facility.
                </p>
              </div>
            ) : (
              <div className="space-y-4 max-w-md">
                {/* Image preview/placeholder now handled within ImageUploader */}
                <ImageUploader
                  entityType="facility-header"
                  entityId={selectedItem?.facilityId || "temp"}
                  currentImageUrl={selectedItem?.headerPhoto || undefined}
                  onUploadComplete={handleLocalHeaderUpload}
                  onUploadError={(err) => setUpdateError(err)}
                  onDeleteImage={() => {
                    setSelectedItem((prev: FacilityItem | null) =>
                      prev ? { ...prev, headerPhoto: null } : null
                    );
                    setPendingHeaderPhotoKey(null); // Clear pending upload if image is deleted
                  }}
                  disabled={isUpdating}
                />
              </div>
            )}
          </div>
        </div>
      </DataManagementSidebar>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={() =>
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))
        }
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        cancelText={confirmationDialog.cancelText}
      />
    </div>
  );
};

export default FacilitiesData;
